import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { HorizontalAlignmentType, VerticalAlignmentType } from "recharts/types/component/DefaultLegendContent";
import { LayoutType } from "recharts/types/util/types";

import { FormControl, FormControlLabel, Box, RadioGroup, Radio, useTheme, Stack, Divider, Typography } from "@mui/material";
import { PieChart, SquareRounded } from '@mui/icons-material';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import { Agg } from "system/query/Aggs";
import TabbedDialog from "components/dialogs/TabbedDialog";
import PieChartPreview from "./PieChartPreview";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";

const defaultWidgetRequest = {
  name: 'pie-chart',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultPieChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundKeywordField = queryInfo.fields?.find((field:QueryField) => {
    const numberOfOptions = parseInt(field?.options?.length + "");
    return ["keyword", "keywords"].includes(field.type) && numberOfOptions;
  }) || false;

  const firstOption = foundKeywordField?.options?.[0];
  if (!foundKeywordField || !firstOption || !firstOption.id || !firstOption.label) {
    return undefined;
  }

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  requestInitBody.aggs = {
      [firstOption.label]: {
        "op": "keyword",
        "field": foundKeywordField.id,
          "aggs": {
            "percent": {
              "op": "percent",
              "of_total": true
            }
          }
      }
    };

  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export type PieChartSettingsStateType = {
  customWidgetTitle: string;
  innerRadius: number;
  labelType: string;
  legendDirection: LayoutType;
  legendPosition: { align: HorizontalAlignmentType, verticalAlign: VerticalAlignmentType };
  maximumSlices: number;
  minimumPortion: number;
  showLabels: boolean;
  showLegend: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: PieChartSettingsStateType = {
  customWidgetTitle: '',
  innerRadius: 0,
  labelType: 'distribution',
  legendDirection: 'horizontal',
  legendPosition: { align: 'center', verticalAlign: 'bottom' },
  maximumSlices: 10,
  minimumPortion: 0,
  showLabels: true,
  showLegend: false,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
}

const legendDirectionOptions: {label: string, value: string}[] = [
  {label: "Horizontal", value: "horizontal"},
  {label: "Vertical", value: "vertical"},
];

const legendButtonGrid: { align?: string, verticalAlign?: string, custom?: any }[] = [
  { align: "Left", verticalAlign: "Top" },
  { align: "Center", verticalAlign: "Top" },
  { align: "Right", verticalAlign: "Top" },
  { align: "Left", verticalAlign: "Middle" },
  { custom: <PieChart /> },
  { align: "Right", verticalAlign: "Middle" },
  { align: "Left", verticalAlign: "Bottom" },
  { align: "Center", verticalAlign: "Bottom" },
  { align: "Right", verticalAlign: "Bottom" },
];

export default function PieChartSettingsDialog(props: WidgetSettingsProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'pie-chart');
  const requestBody = originalRequest?.requestInit?.body || {};

  const [aggsLabel, setAggsLabel] = useState<string>(Object.keys(requestBody?.aggs || {})[0]);
  const [localAggs, setLocalAggs] = useState<Agg | undefined>(requestBody?.aggs?.[aggsLabel]);
  const [legendPosition, setLegendPosition] = useState<{ align: string, verticalAlign: string }>(props.widget?.data?.settings?.legendPosition || { align: "center", verticalAlign: "bottom" });

  const includeOnlyRowsWithKeyword = !!requestBody?.query;

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const label:string = aggsLabel || localAggs?.field || "_total";
    const requestInitBody = { aggs: {} };

    if (localAggs) {
      requestInitBody.aggs = { [label]: localAggs };
      if (includeOnlyRowsWithKeyword && localAggs?.field) {
        requestInitBody.query = {
          op: "not",
          values: [{
            "op": "contains", "value": null, "field": `${localAggs?.field}`
          }]
        };
      } else {
        delete requestInitBody.query;
      }
    }
    widgetRequest.requestInit = { body: requestInitBody };

    props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }


  const labelTypeOptions: OptionsValueType[] = useMemo(() => (
    [
      { label: i18n.t("distribution"), value: 'distribution' },
      { label: i18n.t("values"), value: 'values' },
    ]
  ), []);

  const [settingsState, setSettingsState] = useState<PieChartSettingsStateType>({...defaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: PieChartSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.pie-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["keyword"]}
              queryInfo={props.queryInfo}
              aggs={localAggs}
              setAggs={(newAggs: Agg | null) => setLocalAggs(newAggs || undefined)}
              setAggsLabel={setAggsLabel}
              aggsLabel={aggsLabel}
              disableKeywordFilter={true} />
          </>)}
        </>)}

        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <PieChartPreview settingsState={settingsState} />

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle} />
                  </Stack>}
                  <SettingsToggleButton
                    keyid="widgetTitleAlignment"
                    label={i18n.t("widget-settings.alignment")}
                    optionsList={horizontalAlignmentOptions}
                    value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>

              {/* Graph Options */}
              <SettingsAccordion label={i18n.t("graph")} showBottomDivider={true}>
                {/* Inner Radius */}
                <SettingsSlider
                  defaultValue={0}
                  keyid="innerRadius"
                  label={i18n.t("inner-radius")}
                  max={70}
                  min={0}
                  sliderValue={settingsState.innerRadius}
                  step={10} />

                {/* Maximum Slice Count */}
                <SettingsSlider
                  defaultValue={10}
                  keyid="maximumSlices"
                  label={i18n.t("widget-types.pie-chart.settings.max-slices")}
                  max={10}
                  min={1}
                  showCurrentValue={true}
                  sliderValue={settingsState.maximumSlices}
                  step={1} />

                {/* Minimum Portion % value */}
                  <SettingsSlider
                    defaultValue={10}
                    endCharacter="%"
                    keyid="minimumPortion"
                    label={i18n.t("widget-types.pie-chart.settings.min-portion")}
                    marks={[{ value: 0, label: '0' }, { value: 99, label: '99' }]}
                    max={50}
                    min={0}
                    showCurrentValue={true}
                    sliderValue={settingsState.minimumPortion}
                    step={1} />

                {/* Show Labels */}
                <SettingsCheckbox
                  checked={settingsState.showLabels}
                  keyid="showLabels"
                  label={i18n.t("widget-settings.show-labels")} />
                {settingsState.showLabels &&
                <>
                  {/* Label Display Type */}
                  <SettingsSingleSelect
                    defaultValue={labelTypeOptions.find((item: OptionsValueType) => (
                      item.value === 'percent'
                    )) ?? labelTypeOptions[0]}
                    keyid="labelType"
                    label={i18n.t("widget-types.pie-chart.settings.label-type")}
                    options={labelTypeOptions}
                    value={labelTypeOptions.find((item: OptionsValueType) => (
                      item.value === settingsState.labelType
                    ))}
                  />
                </>}
              </SettingsAccordion>

              {/* Legend Settings */}
              <SettingsAccordion label={i18n.t("legend")} showBottomDivider={false}>
                <SettingsCheckbox
                  checked={settingsState.showLegend}
                  keyid="showLegend"
                  label={i18n.t("widget-settings.show-legend")} />
                {settingsState.showLegend &&
                  <>
                    <SettingsSingleSelect
                      defaultValue={legendDirectionOptions.find((item: OptionsValueType) => (
                        item.value === settingsState.legendDirection
                      )) ?? legendDirectionOptions[0]}
                      keyid="legendDirection"
                      label={i18n.t("widget-settings.legend-direction")}
                      options={legendDirectionOptions} />
                    <Stack direction="row">
                      <Typography sx={{ flex: 1 }}>{i18n.t("widget-settings.legend-position")}</Typography>
                      <Box sx={{ flex: 2, marginLeft: 3 }}>
                        <FormControl>
                          <RadioGroup
                            value={`${legendPosition.verticalAlign} ${legendPosition.align}`}
                            className="radio-group"
                            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}
                            onChange={(event: React.ChangeEvent, value: string) => {
                              const newLegendPosition = Object.assign({}, legendPosition);
                              newLegendPosition.align = value.split(" ")[1].toLocaleLowerCase();
                              newLegendPosition.verticalAlign = value.split(" ")[0].toLocaleLowerCase();
                              setLegendPosition({
                                ...legendPosition,
                                align: value.split(" ")[1].toLocaleLowerCase(),
                                verticalAlign: value.split(" ")[0].toLocaleLowerCase()
                              });
                              setSettingsState({
                                ...settingsState,
                                legendPosition: {
                                  align: value.split(" ")[1].toLocaleLowerCase() as HorizontalAlignmentType,
                                  verticalAlign: value.split(" ")[0].toLocaleLowerCase() as VerticalAlignmentType
                                }
                              })
                            }}>
                            {legendButtonGrid.map((item, index) => {
                              if (item.verticalAlign && item.align) {
                                return (
                                  <FormControlLabel
                                    key={index}
                                    className={
                                      `radio-label ${(item.verticalAlign.toLowerCase() === legendPosition.verticalAlign &&
                                        item.align.toLowerCase() === legendPosition.align) && "checked"}`
                                    }
                                    sx={{
                                      "border": `1px solid ${theme.colors.alpha.black[30]}`,
                                      "borderRadius": `${theme.general.borderRadiusSm}`,
                                      '&.MuiFormControlLabel-root.checked': {
                                        background: "rgb( 34, 170, 255)"
                                      },
                                      '&.MuiFormControlLabel-root span svg': {
                                        visibility: "hidden"
                                      }
                                    }}
                                    label=""
                                    value={`${item.verticalAlign} ${item.align}`}
                                    control={(
                                      <Radio icon={<CheckBoxOutlineBlankRoundedIcon />} checkedIcon={<SquareRounded />} />
                                    )}
                                  />
                                )
                              } else {
                                return (
                                  <FormControlLabel
                                    key={index}
                                    className="non-radio-icon"
                                    disabled
                                    sx={{ justifyContent: "center" }}
                                    label=""
                                    value=""
                                    control={(item.custom)}
                                  />
                                )
                              }
                            })}
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    </Stack>
                  </>}
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  )
}
